import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { useSelector } from '@xstate/react'
import { useAppSelector } from 'hooks/reduxHooks'
import { sortBy } from 'lib/array/arrayUtils'
import { getCartItemDate } from 'components/Pages/CartReviewPage/CartReviewUtils'
import { getUserCartTotals } from 'lib/userCart/userCartTotals'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { CHECKOUT_ITEM_TYPE_LUXURY_PLUS_JOINING_FEE } from 'constants/checkout'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import { MultiCartReviewMachineContext } from './MultiCartReviewMachineContext'
import ModalContext from 'contexts/ModalContext'
import Group from 'components/utils/Group'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import FormatCurrency from 'components/Common/FormatCurrency'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import TextButton from 'LuxKit/Button/TextButton'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import LineShoppingCartIcon from 'LuxKit/Icons/line/LineShoppingCartIcon'
import UserMultiCartSection from './UserMultiCartSection'
import SharedCartsCartSelectionModal from './Modals/SharedCartsCartSelectionModal'
import useAllUserMultiCartItemsMetadata from './hooks/useAllUserMultiCartItemsMetadata'
import useFetchMultiCartHotelItemData from './hooks/useFetchMultiCartHotelItemData'
import useFetchMultiCartExperienceItemData from './hooks/useFetchMultiCartExperienceItemData'
import useUserMultiCartSubscriptionItemData from './hooks/useUserMultiCartSubscriptionItemData'
import { selectItemsInActiveCart } from 'machines/cart/selectors'
import useDelayedTogglingBoolean from 'hooks/useDelayedTogglingBoolean'

const CartDropdownHeader = styled.div`
  padding: ${rem(8)} ${rem(12)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(10)};
  background-color: ${props => props.theme.palette.neutral.default.seven};
`

const CartDropdownContent = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.five};

  & > div {
    gap: ${rem(2)};
  }
`

interface Props {
  open: boolean
  onClose: () => void
  buttonRef: React.RefObject<HTMLButtonElement | null>
  hasDismissButton?: boolean
}

function MultiCartDropdownPreview(props: Props) {
  const { open, onClose, buttonRef, hasDismissButton } = props
  const intl = useIntl()

  // Delay hiding the items while the dropdown fades out
  const renderItems = useDelayedTogglingBoolean(open, { offDelay: 300 })

  const multiCartActorRef = MultiCartContext.useActorRef()
  const showModal = useContext(ModalContext)

  const cartItems = useSelector(multiCartActorRef, selectItemsInActiveCart)

  const activeCartName = useSelector(multiCartActorRef, (snapshot) => {
    if (snapshot.context.cartSummary.status !== 'loaded' || !snapshot.context.activeCartId) {
      return undefined
    }

    return snapshot.context.cartSummary.data[snapshot.context.activeCartId]?.name
  })

  const numberOfActiveItems = useSelector(multiCartActorRef, (snapshot) => {
    return selectItemsInActiveCart(snapshot).filter(item => item.state === 'active').length
  })

  const hasLuxPlusItemInCart = useSelector(multiCartActorRef, (snapshot) => {
    return selectItemsInActiveCart(snapshot).some(item => item.item.itemType === CHECKOUT_ITEM_TYPE_LUXURY_PLUS_JOINING_FEE)
  })

  const cartCount = useSelector(multiCartActorRef, (snapshot) => {
    if (snapshot.context.cartSummary.status !== 'loaded') {
      return 0
    }

    return Object.values(snapshot.context.cartSummary.data)
      .length
  })

  const sortedCartItems = useMemo(
    () => sortBy(cartItems, (item) => getCartItemDate(item.item), 'asc'),
    [cartItems],
  )

  const { fetching, error, items: cartItemsData } = useAllUserMultiCartItemsMetadata(cartItems)

  useFetchMultiCartHotelItemData()
  useFetchMultiCartExperienceItemData()
  useUserMultiCartSubscriptionItemData()

  const loading = fetching && !error
  const isLuxPlusMember = useAppSelector(getCanRedeemLuxPlusBenefits)
  const hasLuxPlusBenefits = isLuxPlusMember || hasLuxPlusItemInCart

  const { totalPrice } = useMemo(
    () => getUserCartTotals(cartItemsData, hasLuxPlusBenefits),
    [cartItemsData, hasLuxPlusBenefits],
  )

  const handleChangeCart = useCallback(() => {
    showModal(<SharedCartsCartSelectionModal multiCartItemIdsToMove={[]} />)
    onClose()
  }, [showModal, onClose])

  return (
    <MultiCartReviewMachineContext.Provider
      options={{
        input: {
          multiCartMachineActorRef: multiCartActorRef,
        },
      }}
    >
      <DropdownSheet
        open={open}
        size="M"
        autoHeight
        noPaddingOnBody
        anchorRef={buttonRef}
        triggerRef={buttonRef}
        placement="bottom-end"
        onClose={onClose}
        hasDismissButton={!loading && hasDismissButton}
        anchorOffset={0}
        primaryActionProps={{
          children: intl.formatMessage(
            {
              id: 'tTU56SueZ02Cx2TLZsM3',
              defaultMessage: 'View cart ({count, plural, one {# item} other {# items}})',
              description: 'Button text to view cart with item count in the cart view',
            },
            { count: numberOfActiveItems },
          ),
          to: '/cart',
          onClick: onClose,
        }}
        footerStart={<>
          <LoadingIndicator visible={loading} inline floating opaque />
          <div>
            <Caption variant="medium" weight="bold">{intl.formatMessage({
              id: 'uPe81IBsogGG2E6HMKSV',
              defaultMessage: 'Cart total:',
              description: 'Label for the cart total price in the cart dropdown preview',
            })}
            </Caption>
            <BodyText variant="large" weight="bold"><FormatCurrency value={totalPrice} /></BodyText>
          </div>
        </>}
      >
        <CartDropdownContent>
          <CartDropdownHeader>
            <Group direction="horizontal" gap={8} verticalAlign="center">
              <LineShoppingCartIcon />
              <BodyText variant="medium" weight="bold">{activeCartName}</BodyText>
            </Group>
            { cartCount > 1 && <TextButton
              kind="tertiary"
              size="small"
              onClick={handleChangeCart}
            >
              {intl.formatMessage({
                id: 'A5Egfk2KEhpoT0IW-miP',
                defaultMessage: 'Change cart',
                description: 'Button text to switch to a different cart in the cart view',
              })}
            </TextButton>}
          </CartDropdownHeader>
          {renderItems && <UserMultiCartSection
            multiCartItems={sortedCartItems}
            cartItemsData={cartItemsData}
            tileVariant="condensed"
          />}
        </CartDropdownContent>
      </DropdownSheet>
    </MultiCartReviewMachineContext.Provider>
  )
}

export default MultiCartDropdownPreview
